import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { AutoCompleteSelectUI, PhoneBase, TabsUI } from '../../Interface';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { useLoader } from '../../../provider/LoaderProvider';
import { WmsType } from '../../../config/wms/wmsType';
import wmsBaseService from '../../../service/wms/wmsBase.service';
import itemService from '../../../service/item.service';
import ItemsDialog from '../../ProductionMaterial/ItemsDialog';
import ItemOutgoing from '../Components/ItemOutgoing';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import { calculateWmsStockValidation } from '../../../utils/wmsLineStockValidation';
import DeliveryBaseDocTab from './DeliveryBaseDocTab';
import WmsTabContent from '../PurchaseDelivery/WmsTabContent';
import TargetDocTab from '../PurchaseDelivery/TargetDocTab';

const wmsType = WmsType.Delivery;

const DeliveryDoc = () => {
  const navigate = useNavigate();
  const { wmsEntry } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const itemInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [head, setHead] = useState(null);
  const [lines, setLines] = useState([]);
  const [baseDocLines, setBaseDocLines] = useState([]);
  const [itemFilter, setItemFilter] = useState('');
  const [items, setItems] = useState([]);
  const [identifiedItem, setIdentifiedItem] = useState(null);
  const [identifiedQty, setIdentifiedQty] = useState(1);
  const [warehouseData, setWarehouseData] = useState([]);
  const [sourceBinId, setSourceBinId] = useState(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [selectedBaseLine, setSelectedBaseLine] = useState(null);

  const resolvedHeadId = useMemo(() => Number(wmsEntry), [wmsEntry]);

  const fetchBaseDocLines = async (baseId = head?.baseId) => {
    if (!baseId) {
      setBaseDocLines([]);
      return;
    }

    const baseRes = await wmsBaseService.getBaseViewByWmsId(baseId, { wmsType });
    const nextBaseDocLines = Array.isArray(baseRes?.rows)
      ? baseRes.rows
      : Array.isArray(baseRes?.docLine)
        ? baseRes.docLine
        : Array.isArray(baseRes)
          ? baseRes
          : [];
    setBaseDocLines(nextBaseDocLines);
  };

  const fetchHeadAndLines = async () => {
    if (!resolvedHeadId) return;

    showLoader();
    try {
      const [headRes, linesRes] = await Promise.all([
        wmsBaseService.getRow(resolvedHeadId, { wmsType }),
        wmsBaseService.getLinesByHeadId(resolvedHeadId, { wmsType }),
      ]);

      const nextHead = headRes?.row ?? headRes?.data ?? headRes ?? null;
      const nextLines = Array.isArray(linesRes?.rows) ? linesRes.rows : Array.isArray(linesRes) ? linesRes : [];

      setHead(nextHead);
      setLines(nextLines);
      await fetchBaseDocLines(nextHead?.baseId);
    } catch (e) {
      addNotification({
        content: 'A dokumentum adatai nem tölthetők be.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchHeadAndLines();
  }, [resolvedHeadId]);

  useEffect(() => {
    masterDataService.warehouseToForm().then((data) => {
      setWarehouseData(data ?? []);
    });
  }, []);

  useEffect(() => {
    setReference(head?.reference ?? '');
    setNote(head?.note ?? '');
  }, [head]);

  const sourceWarehouse = useMemo(
    () => warehouseData.find((warehouse) => warehouse.code === head?.whsCode) ?? null,
    [warehouseData, head?.whsCode]
  );
  const isSourceBinManaged = Boolean(sourceWarehouse?.binManaged);
  const sourceBins = sourceWarehouse?.bin ?? [];
  const sourceBinOptions = useMemo(() => generateAutocompleteItems(sourceBins, 'binCode', 'binId'), [sourceBins]);
  const selectedSourceBin = sourceBins.find((bin) => bin.binId === sourceBinId) ?? null;

  useEffect(() => {
    if (!isSourceBinManaged) {
      setSourceBinId(null);
      return;
    }

    const hasSelectedBin = sourceBins.some((bin) => bin.binId === sourceBinId);
    if (!hasSelectedBin) {
      setSourceBinId(head?.binId ?? null);
    }
  }, [isSourceBinManaged, sourceBins, sourceBinId, head?.binId]);

  const resolveMatchedBaseLine = (selectedItem) => {
    const normalizedItemCode = String(selectedItem?.itemCode ?? '')
      .trim()
      .toLowerCase();
    if (!normalizedItemCode) return null;

    const preferredBaseLine = (baseDocLines ?? []).find((line) => {
      const sameLine = Number(line?.lineNum) === Number(selectedBaseLine?.lineNum);
      const sameItemCode =
        String(line?.itemCode ?? '')
          .trim()
          .toLowerCase() === normalizedItemCode;
      const openQty = Number(line?.qtyDiff ?? line?.qntQty ?? 0) || 0;

      return sameLine && sameItemCode && openQty > 0;
    });

    if (preferredBaseLine) {
      return preferredBaseLine;
    }

    return (baseDocLines ?? []).find((line) => {
      const lineItemCode = String(line?.itemCode ?? '')
        .trim()
        .toLowerCase();
      if (!lineItemCode || lineItemCode !== normalizedItemCode) return false;

      const openQty = Number(line?.qtyDiff ?? line?.qntQty ?? 0) || 0;
      return openQty > 0;
    });
  };

  const handleAddLine = async (selectedItem, qtyOverride = null) => {
    if (!selectedItem?.itemCode) return;

    const resolvedQty = Number(qtyOverride ?? selectedItem?.quantity ?? selectedItem?.qty ?? 1) || 1;
    const quantityUnitId =
      selectedItem?.quantityUnitId ?? selectedItem?.item?.quantityUnit?.id ?? selectedItem?.quantityUnit?.id ?? null;
    const quantityUnitCode =
      selectedItem?.quantityUnitCode ??
      selectedItem?.item?.quantityUnit?.code ??
      selectedItem?.quantityUnit?.code ??
      null;
    const matchedBaseLine = resolveMatchedBaseLine(selectedItem);

    showLoader();
    try {
      await wmsBaseService.createLine({
        id: undefined,
        wmsType,
        wmsHeadId: resolvedHeadId,
        itemCode: selectedItem.itemCode,
        quantity: resolvedQty,
        baseLine: matchedBaseLine?.lineNum ?? null,
        baseId: head?.baseId ?? null,
        baseDocNum: head?.baseDocNum ?? null,
        baseDocType: head?.baseDocType ?? null,
        whsCode: head?.whsCode ?? selectedItem?.whsCode ?? null,
        binId: selectedItem?.binId ?? sourceBinId ?? null,
        batchSn: selectedItem?.batchSn ?? null,
        batchSnId: selectedItem?.batchSnId ?? selectedItem?.batchId ?? selectedItem?.snBinId ?? null,
        binCode: selectedItem?.binCode ?? selectedSourceBin?.binCode ?? null,
        quantityUnitId,
        quantityUnitCode,
      });

      setItemFilter('');
      setItems([]);
      setIdentifiedItem(null);
      setIdentifiedQty(1);
      setSelectedBaseLine(null);
      await fetchHeadAndLines();
      setTimeout(() => {
        if (itemInputRef.current) itemInputRef.current.focus();
      }, 0);
    } catch (e) {
      addNotification({
        content: 'A tétel rögzítése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleSearchItem = async () => {
    const query = itemFilter.trim();
    if (!query) return;

    if (isSourceBinManaged) {
      const normalizedQuery = query.toLowerCase();
      const matchedBin = sourceBins.find(
        (bin) =>
          String(bin?.binCode ?? '')
            .trim()
            .toLowerCase() === normalizedQuery
      );

      if (matchedBin) {
        setSourceBinId(matchedBin.binId);
        setItemFilter('');
        setSelectedBaseLine(null);
        addNotification({
          content: `Kiadó rakhely kiválasztva: ${matchedBin.binCode}`,
          type: NotificationType.SUCCESS,
        });
        return;
      }

      if (!sourceBinId) {
        addNotification({
          content: 'A kiadó raktár rakhelyes, előbb válassz kiadó rakhelyet.',
          type: NotificationType.WARNING,
        });
        return;
      }
    }

    showLoader();
    try {
      const data = await itemService.findItemFromStock({
        query,
        fullSearch: false,
        whsCode: head?.whsCode,
        binId: sourceBinId ?? undefined,
      });

      if (!Array.isArray(data) || data.length === 0) {
        addNotification({
          content: `Nincs találat a megadott cikkre. (${query})`,
          type: NotificationType.WARNING,
        });
        return;
      }

      setSelectedBaseLine(null);

      if (data.length === 1) {
        setIdentifiedItem(data[0]);
        setIdentifiedQty(1);
        setItemFilter('');
        return;
      }

      setItems(data);
      setItemFilter('');
    } catch (e) {
      addNotification({
        content: 'A cikk keresése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleRecognizeBaseDocItem = async (line) => {
    const itemCode = String(line?.itemCode ?? '').trim();
    if (!itemCode) {
      addNotification({ content: 'A sor nem tartalmaz cikkszámot.', type: NotificationType.WARNING });
      return;
    }

    if (isSourceBinManaged && !sourceBinId) {
      addNotification({
        content: 'A kiadó raktár rakhelyes, ezért a kiadó rakhely megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    showLoader();
    try {
      const data = await itemService.findItemFromStock({
        query: itemCode,
        fullSearch: false,
        whsCode: head?.whsCode,
        binId: sourceBinId ?? undefined,
      });

      if (!Array.isArray(data) || data.length === 0) {
        addNotification({ content: `A cikk nem található készleten: ${itemCode}`, type: NotificationType.WARNING });
        return;
      }

      setSelectedBaseLine({
        lineNum: line?.lineNum ?? null,
        itemCode,
      });

      if (data.length === 1) {
        setIdentifiedItem(data[0]);
        setIdentifiedQty(1);
        setItemFilter('');
        addNotification({ content: `Cikk felismerve: ${data[0]?.itemCode}`, type: NotificationType.SUCCESS });
        return;
      }

      setItems(data);
      setItemFilter('');
      addNotification({ content: `Több készletes tétel található: ${itemCode}`, type: NotificationType.SUCCESS });
    } catch (e) {
      addNotification({ content: 'A cikk felismerése sikertelen.', type: NotificationType.ERROR });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteLine = async (line) => {
    const lineId = line?.id;
    if (!lineId) return;

    showLoader();
    try {
      await wmsBaseService.deleteLine(lineId, wmsType);
      await fetchHeadAndLines();
    } catch (e) {
      addNotification({
        content: 'A sor törlése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleConfirmIdentifiedItem = async () => {
    if (!identifiedItem?.itemCode) {
      addNotification({
        content: 'Nincs kiválasztott cikk.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (isSourceBinManaged && !sourceBinId) {
      addNotification({
        content: 'A kiadó raktár rakhelyes, ezért a kiadó rakhely megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (Number(identifiedQty) <= 0) {
      addNotification({
        content: 'A mennyiségnek 0-nál nagyobbnak kell lennie.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (isOverStockLimit) {
      addNotification({
        content: 'A rögzített és rögzítendő mennyiség együtt nem haladhatja meg a készletet.',
        type: NotificationType.WARNING,
      });
      return;
    }

    await handleAddLine(identifiedItem, identifiedQty);
  };

  const identifiedQtyUnitCode =
    identifiedItem?.quantityUnitCode ??
    identifiedItem?.item?.quantityUnit?.code ??
    identifiedItem?.quantityUnit?.code ??
    '';

  const { identifiedStockQty, alreadyRecordedQty, nextTotalQty, isOverStockLimit } = useMemo(
    () =>
      calculateWmsStockValidation({
        identifiedItem,
        lines,
        identifiedQty,
      }),
    [identifiedItem, lines, identifiedQty]
  );

  const openQty = Math.max(identifiedStockQty - alreadyRecordedQty, 0);

  const handleSaveDoc = async () => {
    if (!head || !resolvedHeadId) return;

    showLoader();
    try {
      await wmsBaseService.generateTargetDoc(resolvedHeadId, {
        wmsType,
        reference,
        note,
      });

      navigate('/app/wms/delivery');

      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
    } catch (e) {
      addNotification({
        content: 'A bizonylat mentése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const tabs = [
    ...(head?.baseId
      ? [
          {
            id: 0,
            name: 'QUANT',
            content: <DeliveryBaseDocTab baseDocLines={baseDocLines} onRecognizeItem={handleRecognizeBaseDocItem} />,
          },
        ]
      : []),
    {
      id: 1,
      name: 'WMS',
      content: <WmsTabContent lines={lines} onDeleteLine={handleDeleteLine} />,
    },
    {
      id: 2,
      name: 'Lezárás',
      content: (
        <TargetDocTab
          reference={reference}
          onReferenceChange={setReference}
          note={note}
          onNoteChange={setNote}
          onSave={handleSaveDoc}
        />
      ),
    },
  ];

  return (
    <PhoneBase name="DeliveryDoc">
      <Typography variant="h5" sx={{ fontWeight: 400, py: 1, px: 2 }}>
        Kiszállítás
      </Typography>
      <Box sx={{ px: 2, pb: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Raktár: {head?.whsCode ?? '-'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                Vevő: {head?.companyCode ?? head?.companyName ?? head?.companyId ?? '-'}
              </Typography>
              {head?.baseDocNum ? (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Bizonylat: {head.baseDocNum}
                </Typography>
              ) : null}
            </Grid>

            {isSourceBinManaged ? (
              <Grid item xs={9} md="auto" sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Box sx={{ minWidth: 200, width: '100%', maxWidth: 360, bgcolor: 'white' }}>
                  <AutoCompleteSelectUI
                    selectedValue={sourceBinId}
                    selectedLabelValue={selectedSourceBin?.binCode ?? ''}
                    onChange={(_e, newVal) => {
                      setSourceBinId(newVal?.value ?? null);
                    }}
                    dataset={sourceBinOptions}
                    placeholder="Rakhely"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <ItemOutgoing
          itemFilter={itemFilter}
          onItemFilterChange={setItemFilter}
          onSearch={handleSearchItem}
          itemInputRef={itemInputRef}
          identifiedItem={identifiedItem}
          identifiedQty={identifiedQty}
          onIdentifiedQtyChange={(rawValue) => {
            if (rawValue === '') {
              setIdentifiedQty('');
              return;
            }

            const parsedQty = Number(rawValue);
            if (Number.isNaN(parsedQty)) return;

            setIdentifiedQty(Math.max(1, parsedQty));
          }}
          onIdentifiedQtyBlur={() => {
            if (identifiedQty === '' || Number(identifiedQty) <= 0) {
              setIdentifiedQty(1);
            }
          }}
          identifiedQtyUnitCode={identifiedQtyUnitCode}
          onConfirmItem={handleConfirmIdentifiedItem}
          onClearIdentified={() => {
            setIdentifiedItem(null);
            setIdentifiedQty(1);
            setSelectedBaseLine(null);
          }}
          identifiedStockQty={identifiedStockQty}
          alreadyRecordedQty={alreadyRecordedQty}
          openQty={openQty}
          isOverStockLimit={isOverStockLimit}
        />
      </Box>

      <TabsUI activeTab={activeTab} handleTabClick={setActiveTab} disablePadding>
        {tabs.map((tab) => (
          <div key={tab.id} name={tab.name}>
            {tab.content}
          </div>
        ))}
      </TabsUI>

      <ItemsDialog
        open={items.length > 1}
        handleClose={() => setItems([])}
        items={items}
        addItem={(item) => {
          setIdentifiedItem(item);
          setIdentifiedQty(1);
        }}
      />
    </PhoneBase>
  );
};

export default DeliveryDoc;
